import { ButtonView, Command, ListView, Plugin, createDropdown } from "ckeditor5"

class CaptionAlignCommand extends Command {
  value: string | null = null

  refresh() {
    const imageBlock = this._getSelectedImageBlock()
    this.isEnabled = !!imageBlock
    this.value = imageBlock?.getAttribute("captionAlign") as string | null ?? null
  }

  execute(options: { align?: string }) {
    const alignValue = options.align
    if (!alignValue) return

    const imageBlock = this._getSelectedImageBlock()
    if (!imageBlock) return

    this.editor.model.change((writer) => {
      writer.setAttribute("captionAlign", alignValue, imageBlock)
    })
  }

  private _getSelectedImageBlock() {
    const selection = this.editor.model.document.selection
    const selectedElement = selection.getSelectedElement()

    if (selectedElement?.is("element") && selectedElement.name === "imageBlock") {
      return selectedElement
    }

    const position = selection.getFirstPosition()
    if (position) {
      const parent = position.parent
      if (parent?.is("element") && parent.name === "imageBlock") {
        return parent
      }
      for (const ancestor of parent?.getAncestors() || []) {
        if (ancestor.is("element") && ancestor.name === "imageBlock") {
          return ancestor
        }
      }
    }

    return null
  }
}

export class CaptionAlignment extends Plugin {
  static get pluginName() {
    return "CaptionAlignment"
  }

  init() {
    const editor = this.editor

    editor.model.schema.extend("imageBlock", {
      allowAttributes: ["captionAlign"],
    })

    editor.commands.add("captionAlign", new CaptionAlignCommand(editor))

    this._createDropdown(editor)
    this._injectStyles()
    this._setupConversion()
  }

  afterInit() {
    // Toolbar đã được config trong Ckeditor.tsx
  }

  private _createDropdown(editor: any) {
    const command = editor.commands.get("captionAlign") as CaptionAlignCommand

    editor.ui.componentFactory.add("captionAlignDropdown", (locale: any) => {
      const dropdownView = createDropdown(locale)

      // Button chính
      dropdownView.buttonView.set({
        label: "Vị trí caption",
        tooltip: true,
        withText: true,
      })

      // Cập nhật button khi giá trị thay đổi
      dropdownView.bind("isEnabled").to(command, "isEnabled")

      // Tạo list cho dropdown
      const listView = new ListView(locale)

      // Options
      const options = [
        { align: "left", label: "← Trái", icon: `<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M7 7v10h2V7H7zm8 0v2h4V7h-4z"/></svg>` },
        { align: "center", label: "☰ Giữa", icon: `<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M7 7v10h2V7H7zm4 2v2h2V9h-2zm0 4v2h2v-2h-2z"/></svg>` },
        { align: "right", label: "→ Phải", icon: `<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M7 7v10h2V7H7zm6 0v2h4V7h-4z"/></svg>` },
      ]

      options.forEach((opt) => {
        const button = new ButtonView(locale)
        button.set({
          label: opt.label,
          icon: opt.icon,
          tooltip: true,
          withText: true,
        })

        button.bind("isEnabled").to(command, "isEnabled")
        button.bind("isOn").to(command, "value", (value: string | null) => value === opt.align)

        button.on("execute", () => {
          editor.execute("captionAlign", { align: opt.align })
        })

        listView.items.add(button)
      })

      dropdownView.panelView.children.add(listView)

      return dropdownView
    })
  }

  private _injectStyles() {
    const style = document.createElement("style")
    style.textContent = `
      /* Caption alignment dropdown */
      .ck .ck-dropdown__panel .ck-button { width: 100%; }
      
      /* Figure caption alignment */
      figure.caption-align-center figcaption { text-align: center !important; }
      figure.caption-align-right figcaption { text-align: right !important; }
      figure figcaption { text-align: left !important; }
    `
    document.head.appendChild(style)
  }

  private _setupConversion() {
    const conversion = this.editor.conversion

    conversion.for("editingDowncast").add((dispatcher) => {
      dispatcher.on("attribute:captionAlign:imageBlock", (evt, data, conversionApi) => {
        const viewFigure = conversionApi.mapper.toViewElement(data.item)
        if (!viewFigure) return

        const writer = conversionApi.writer

        writer.removeClass("caption-align-left", viewFigure)
        writer.removeClass("caption-align-center", viewFigure)
        writer.removeClass("caption-align-right", viewFigure)

        if (data.attributeNewValue) {
          writer.addClass(`caption-align-${data.attributeNewValue}`, viewFigure)
        }
      })
    })

    conversion.for("upcast").add((dispatcher) => {
      dispatcher.on("element:figure", (evt, data, conversionApi) => {
        const viewElement = data.viewItem
        const className = viewElement.className || ""

        let alignValue: string | null = null
        if (className.includes("caption-align-left")) alignValue = "left"
        else if (className.includes("caption-align-center")) alignValue = "center"
        else if (className.includes("caption-align-right")) alignValue = "right"

        if (alignValue) {
          const modelElement = data.modelCursor
          if (modelElement && modelElement.is("element") && modelElement.name === "imageBlock") {
            conversionApi.writer.setAttribute("captionAlign", alignValue, modelElement)
          }
        }
      })
    })

    conversion.for("dataDowncast").add((dispatcher) => {
      dispatcher.on("attribute:captionAlign:imageBlock", (evt, data, conversionApi) => {
        const viewFigure = conversionApi.mapper.toViewElement(data.item)
        if (!viewFigure) return

        const writer = conversionApi.writer

        writer.removeClass("caption-align-left", viewFigure)
        writer.removeClass("caption-align-center", viewFigure)
        writer.removeClass("caption-align-right", viewFigure)

        if (data.attributeNewValue) {
          writer.addClass(`caption-align-${data.attributeNewValue}`, viewFigure)
        }
      })
    })
  }
}
